import { useState } from 'react'
import { Loader2, Search } from 'lucide-react'

import type { ShopMedia, ShopMediaKind } from '@/lib/api/shop-media-api'
import { MediaGrid } from '@/components/media/media-grid'
import { MediaUploadButton } from '@/components/media/media-upload'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useDebounced } from '@/hooks/use-debounced'
import { useShopMediaInfinite } from '@/hooks/use-shop-media-query'
import { m } from '@/lib/paraglide/messages'

type KindFilter = ShopMediaKind | 'all'

/**
 * Tìm + lọc + lưới thư viện, dùng chung cho màn Thư viện ảnh và hộp
 * MediaPicker. `kind` cố định thì ẩn bộ lọc loại.
 */
export function MediaBrowser({
    kind,
    selected,
    onPick,
    onUploaded,
}: {
    kind?: ShopMediaKind
    selected?: ReadonlySet<string>
    onPick: (item: ShopMedia) => void
    onUploaded?: (items: ShopMedia[]) => void
}) {
    const [q, setQ] = useState('')
    const [filter, setFilter] = useState<KindFilter>('all')
    const search = useDebounced(q.trim(), 300)
    const effective = kind ?? (filter === 'all' ? undefined : filter)
    const searchLabel =
        kind === 'video'
            ? m.media_search_placeholder_video()
            : m.media_search_placeholder()
    const query = useShopMediaInfinite({
        q: search || undefined,
        kind: effective,
    })
    const items = query.data?.pages.flatMap((p) => p.data ?? []) ?? []

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-48 flex-1">
                    <Search
                        className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                        aria-hidden
                    />
                    <Input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={searchLabel}
                        aria-label={searchLabel}
                        className="pl-8"
                    />
                </div>
                {!kind && (
                    <Tabs
                        value={filter}
                        onValueChange={(v) => setFilter(v as KindFilter)}
                    >
                        <TabsList>
                            <TabsTrigger value="all">
                                {m.media_kind_all()}
                            </TabsTrigger>
                            <TabsTrigger value="image">
                                {m.media_kind_image()}
                            </TabsTrigger>
                            <TabsTrigger value="video">
                                {m.media_kind_video()}
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                )}
                <MediaUploadButton kind={kind} onUploaded={onUploaded} />
            </div>
            {query.isPending ? (
                <div className="flex justify-center py-10">
                    <Loader2
                        className="size-6 animate-spin text-muted-foreground"
                        aria-label={m.media_loading()}
                    />
                </div>
            ) : query.isError ? (
                <p
                    className="py-10 text-center text-sm text-destructive"
                    role="alert"
                >
                    {m.media_load_failed()}
                </p>
            ) : items.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                    {search ? m.media_empty_search() : m.media_empty()}
                </p>
            ) : (
                <MediaGrid
                    items={items}
                    selected={selected}
                    onPick={onPick}
                    hasMore={!!query.hasNextPage}
                    loadingMore={query.isFetchingNextPage}
                    onLoadMore={() => void query.fetchNextPage()}
                />
            )}
        </div>
    )
}
